import { getProxyAuthHeaders } from '@/auth/imsTokenStorage';
import { config } from '@/config/env';
import { SPREADSHEET_MIME } from '@/utils/contactsCsv';
import { downscaleImageToMaxEdge } from '@/utils/imageResize';

import { logger } from '../logger';

import { RestApi, RestApiError } from './RestApi.ts';

/** HTTP status the ui-proxy returns when an upload body exceeds its limit. */
const HTTP_PAYLOAD_TOO_LARGE = 413;

// A network blip can abort the upload mid-transfer: the request never reaches
// the server (no response, nothing logged server-side) and the user sees a hard
// failure for what is usually a one-off. Retry the POST a couple of times so a
// transient drop recovers without the user noticing.
const UPLOAD_TRANSPORT_RETRIES = 2;
// Short pause between attempts. Long enough to ride out a momentary drop, short
// enough that the send button (disabled while uploading) re-enables quickly.
const UPLOAD_RETRY_DELAY_MS = 500;

// A transport failure (connection dropped before any response arrived) surfaces
// from fetch() as a TypeError, whereas a server that did respond non-2xx
// surfaces as a RestApiError carrying the HTTP status. Only the former is worth
// retrying — a real 4xx/5xx would just fail again with the same body. A
// malformed-JSON body surfaces as a SyntaxError, which is also (correctly) not
// retried here.
function isTransportError(error: unknown): boolean {
	return error instanceof TypeError;
}

/** Why a file failed validation, so callers can branch without string-matching. */
export type FileValidationReason = 'size' | 'format';

/** Result of a client-side file validation check. */
export type FileValidationResult = {
	isValid: boolean;
	reason?: FileValidationReason;
	error?: string;
};

/** Thrown when a file is rejected before upload because it exceeds a size cap. */
export class FileTooLargeError extends Error {
	readonly reason: FileValidationReason = 'size';

	constructor(message: string) {
		super(message);
		this.name = 'FileTooLargeError';
	}
}

export const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png'] as const;
export const ALLOWED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png'] as const;

// HEIC/HEIF (iPhone-native captures) are accepted at the input and transcoded to
// JPEG on upload, so they are NOT in the ALLOWED_IMAGE_* sets the image-specific
// validators check — the stored blob is always JPEG/PNG. These are used to widen
// the file picker's `accept` filter and to detect what needs converting.
export const HEIC_IMAGE_EXTENSIONS = ['.heic', '.heif'] as const;
export const HEIC_IMAGE_MIME_TYPES = ['image/heic', 'image/heif'] as const;
const HEIC_EXTENSION_SET = new Set<string>(HEIC_IMAGE_EXTENSIONS);
const HEIC_MIME_TYPE_SET = new Set<string>(HEIC_IMAGE_MIME_TYPES);

export const IMAGE_FILE_INPUT_ACCEPT = [
	...ALLOWED_IMAGE_EXTENSIONS,
	...HEIC_IMAGE_EXTENSIONS,
	...ALLOWED_IMAGE_MIME_TYPES,
	...HEIC_IMAGE_MIME_TYPES,
].join(',');

// Every image MIME type a DropZone should accept. HEIC/HEIF are included
// because the upload path transcodes them to JPEG; keeping this in sync with
// IMAGE_FILE_INPUT_ACCEPT means click-to-browse and drag-and-drop agree.
const ACCEPTED_IMAGE_DROP_MIME_TYPES: readonly string[] = [
	...ALLOWED_IMAGE_MIME_TYPES,
	...HEIC_IMAGE_MIME_TYPES,
];

/**
 * Whether a DropZone drag payload contains an accepted image type. Use in a
 * DropZone's `getDropOperation` so dropped HEIC/HEIF files aren't cancelled.
 */
export function isAcceptedImageDropType(types: {
	has: (type: string) => boolean;
}): boolean {
	return ACCEPTED_IMAGE_DROP_MIME_TYPES.some((mime) => types.has(mime));
}
const ALLOWED_IMAGE_EXTENSION_SET = new Set<string>(ALLOWED_IMAGE_EXTENSIONS);
const ALLOWED_IMAGE_MIME_TYPE_SET = new Set<string>(ALLOWED_IMAGE_MIME_TYPES);
const IMAGE_MIME_TYPE_ALIASES: Record<
	string,
	(typeof ALLOWED_IMAGE_MIME_TYPES)[number]
> = {
	'image/jpg': 'image/jpeg',
	'image/pjpeg': 'image/jpeg',
	'image/x-png': 'image/png',
};

const ALLOWED_EXTENSIONS = new Set([
	...ALLOWED_IMAGE_EXTENSIONS,
	// HEIC/HEIF are accepted at the input and transcoded to JPEG on upload, so
	// the original-file validator (which runs before conversion) must recognize
	// them as valid input rather than rejecting them as an unknown format.
	...HEIC_IMAGE_EXTENSIONS,
	'.mp4',
	'.mov',
	'.csv',
	// Modern Excel workbooks are accepted on the contact-import path; the
	// importer converts the first sheet to CSV client-side before parsing, and
	// the original workbook is uploaded as the recorded source file.
	'.xlsx',
]);
const MAX_IMAGE_SIZE = 30 * 1024 * 1024; // 30MB
const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB
const MAX_CSV_SIZE = 5 * 1024 * 1024; // 5MB

export const MAX_IMAGE_SIZE_LABEL = '30MB';

/**
 * Map an upload error to user-facing copy. Surfaces an actionable "file too
 * large" message when the file was rejected for size — either client-side
 * (FileTooLargeError) or by the ui-proxy's body-parser limit (HTTP 413 carried
 * on RestApiError) — instead of a generic failure.
 */
export function getUploadErrorMessage(error: unknown): string {
	const isTooLarge =
		error instanceof FileTooLargeError ||
		(error instanceof RestApiError && error.status === HTTP_PAYLOAD_TOO_LARGE);
	if (isTooLarge) {
		return `File too large — max ${MAX_IMAGE_SIZE_LABEL}.`;
	}
	// The connection dropped before the upload finished (see isTransportError).
	// Name the real cause so the user knows a plain retry is likely to work,
	// rather than the generic failure that reads as "something is broken."
	if (isTransportError(error)) {
		return 'Upload interrupted — please try again.';
	}
	return 'Upload failed. Please try again.';
}

export type FileUploadParams = {
	userId: string;
	fileName: string;
	fileBuffer: string;
	contentType: string;
};

export type FileUploadResult = {
	success: boolean;
	url: string;
	blobName: string;
	fileName: string;
};

export type FileDeleteParams = {
	userId: string;
	fileName: string;
};

export type UserFile = {
	name: string;
	url: string;
	size: number;
	lastModified: Date;
};

export type FileListResponse = {
	success: boolean;
	files: UserFile[];
};

export class FileUploadService extends RestApi {
	constructor() {
		super(`${config.UI_PROXY_URL}/file-upload-vite-proxy`);
	}

	private getAuthHeaders(): Record<string, string> {
		return getProxyAuthHeaders();
	}

	async upload(
		file: File,
		userId: string,
		customValidation?: (file: File) => boolean,
	): Promise<FileUploadResult> {
		// Validate the ORIGINAL uploaded file (size + format) before transcoding.
		// The size cap must apply to the source bytes the user actually picked,
		// not the converted output.
		const { isValid, reason, error } = this.isValidFile(file, customValidation);

		if (!isValid) {
			if (reason === 'size') {
				throw new FileTooLargeError(error ?? 'File too large');
			}
			throw new Error(error);
		}

		// Convert iPhone-native HEIC/HEIF to JPEG so the stored blob is always
		// web-renderable and accepted downstream (gpt-image-2, previews). JPEG
		// keeps the output small, so it comfortably fits the image cap.
		file = await this.convertHeicToJpeg(file);

		// Downscale an oversized photo before it is persisted, so the stored blob
		// is always within the chat model's per-image pixel limit (ADO-1443). A
		// full-resolution phone panorama otherwise reaches Claude at upload size
		// and fails the whole turn. Images already within the cap, and non-image
		// uploads, pass through byte-identical.
		file = await downscaleImageToMaxEdge(file);

		try {
			const fileBuffer = await this.fileToBase64(file);
			const contentType = this.getContentType(file.name);
			const uniqueFileName = this.generateUniqueFilename(file.name);

			const result = await this.postUploadWithRetry({
				userId,
				fileName: uniqueFileName,
				fileBuffer,
				contentType,
			});

			return result;
		} catch (error) {
			logger.error('Error uploading file:', error);
			// Preserve typed errors (RestApiError carries the HTTP status, e.g.
			// 413 for an over-cap body) so callers can branch on the real cause
			// instead of parsing a flattened message string.
			throw error;
		}
	}

	// POST the prepared upload body, retrying only when the request was aborted
	// in transit (see isTransportError). The body is built once by the caller —
	// only this network call is retried, never the base64/HEIC work. A server
	// that actually responded (e.g. 413, 500) fails fast on the first attempt so
	// its typed status reaches the caller unchanged. The blob name is a stable
	// UUID, so a retry that lands after a partial write is an idempotent
	// overwrite rather than a duplicate.
	private async postUploadWithRetry(
		body: FileUploadParams,
	): Promise<FileUploadResult> {
		const maxAttempts = UPLOAD_TRANSPORT_RETRIES + 1;
		for (let attempt = 1; attempt <= maxAttempts; attempt++) {
			try {
				return await this.post<FileUploadResult>(
					'/upload',
					body,
					// Re-read auth per attempt so a token refreshed between retries
					// is picked up.
					this.getAuthHeaders(),
				);
			} catch (error) {
				if (!isTransportError(error) || attempt === maxAttempts) {
					throw error;
				}
				logger.warn('Upload transport failure, retrying', { attempt });
				await new Promise((resolve) =>
					setTimeout(resolve, UPLOAD_RETRY_DELAY_MS),
				);
			}
		}
		// Unreachable: the loop either returns a result or throws on the last
		// attempt. Satisfies the compiler's return-path analysis.
		throw new Error('Upload retry loop exited without a result');
	}

	async uploadMultiple(
		files: File[],
		userId: string,
	): Promise<FileUploadResult[]> {
		const uploadPromises = files.map((file) => this.upload(file, userId));
		return Promise.all(uploadPromises);
	}

	async getFileByUrl(fileUrl: string, userId: string): Promise<Blob> {
		const encodedFileUrl = encodeURIComponent(fileUrl);
		// Proxy returns an image binary file not json so this.get doesn't work
		const response = await fetch(
			`${this.baseUrl}/get-file?fileUrl=${encodedFileUrl}&userId=${encodeURIComponent(userId)}`,
			{
				headers: this.getAuthHeaders(),
			},
		);

		if (!response.ok) {
			throw new Error(`Failed to fetch file: ${await response.text()}`);
		}

		const blob = await response.blob();
		return blob;
	}

	async deleteFile(userId: string, fileName: string): Promise<boolean> {
		try {
			const result = await this.post<{ success: boolean }>(
				'/delete',
				{
					userId,
					fileName,
				},
				this.getAuthHeaders(),
			);

			return result.success;
		} catch (error) {
			logger.error('Error deleting file:', error);
			return false;
		}
	}

	async listUserFiles(userId: string): Promise<UserFile[]> {
		if (!userId) {
			throw new Error('userId is required');
		}

		try {
			const result = await this.get<FileListResponse>(
				`/list?userId=${userId}`,
				this.getAuthHeaders(),
			);

			return result.files.map((file) => ({
				...file,
				lastModified: new Date(file.lastModified),
			}));
		} catch (error) {
			logger.error('Error listing user files:', error);
			throw new Error(
				`Failed to list files: ${error instanceof Error ? error.message : 'Unknown error'}`,
			);
		}
	}

	// Generate a temporary read URL for Instagram posting and UI rendering.
	async generateAccessUrlFromFileUrl(
		fileUrl: string,
		userId: string,
	): Promise<string> {
		try {
			const result = await this.post<{
				success: boolean;
				sasUrl: string;
				expiresAt?: string;
			}>(
				'/sas-url',
				{
					fileUrl,
					userId,
				},
				this.getAuthHeaders(),
			);

			return result.sasUrl;
		} catch (error) {
			logger.error('Error generating blob access URL:', error);
			throw new Error(
				`Failed to generate blob access URL: ${error instanceof Error ? error.message : 'Unknown error'}`,
			);
		}
	}

	private async fileToBase64(file: File): Promise<string> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => {
				const result = reader.result as string;
				// Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
				const base64 = result.split(',')[1];
				resolve(base64);
			};
			reader.onerror = reject;
			reader.readAsDataURL(file);
		});
	}

	private getContentType(fileName: string): string {
		const contentTypes: { [key: string]: string } = {
			jpg: 'image/jpeg',
			jpeg: 'image/jpeg',
			png: 'image/png',
			mp4: 'video/mp4',
			mov: 'video/quicktime',
			csv: 'text/csv',
			xlsx: SPREADSHEET_MIME,
		};

		const ext = fileName.toLowerCase().split('.').pop() || '';
		return contentTypes[ext] || 'application/octet-stream';
	}

	isVideoFile(filename: string): boolean {
		const ext = this.getFileExtension(filename);
		return ext === '.mp4' || ext === '.mov';
	}

	isHeicFile(file: File): boolean {
		const ext = this.getFileExtension(file.name);
		const mime = file.type.trim().toLowerCase();
		return HEIC_EXTENSION_SET.has(ext) || HEIC_MIME_TYPE_SET.has(mime);
	}

	/**
	 * Transcode a HEIC/HEIF file to JPEG in the browser. Most browsers can't
	 * decode HEIC, so we use heic2any (libheif/wasm). Returns the original file
	 * unchanged when it isn't HEIC. Throws a user-facing error if conversion
	 * fails, so we never silently upload an undecodable blob.
	 *
	 * We target JPEG (not PNG): HEIC is a compressed photo, so JPEG keeps the
	 * output small (~1-3MB), while a lossless PNG balloons to the raw bitmap
	 * (a ~1MB HEIC -> ~46MB PNG) and blows past the 30MB cap. Photos
	 * have no alpha, so JPEG loses nothing that matters and matches the backend.
	 *
	 * Guards the raw HEIC size *before* the WASM decode: heic2any pulls the whole
	 * bitmap into memory, so an oversized HEIC would otherwise pay the full
	 * (expensive) decode cost only to be rejected afterwards.
	 */
	async convertHeicToJpeg(file: File): Promise<File> {
		if (!this.isHeicFile(file)) {
			return file;
		}

		if (file.size > MAX_IMAGE_SIZE) {
			const sizeMB = MAX_IMAGE_SIZE / (1024 * 1024);
			throw new FileTooLargeError(
				`File size exceeds maximum allowed size of ${sizeMB}MB for images`,
			);
		}

		try {
			// Lazy-load: heic2any pulls in a sizeable wasm decoder, so keep it out
			// of the main bundle and only fetch it when a HEIC is actually picked.
			const { default: heic2any } = await import('heic2any');
			// heic2any returns Blob[] for multi-image HEIC containers (e.g. burst
			// or Live Photos); a single still returns one Blob. Take the first
			// frame so we never wrap an array into File() (which would stringify
			// to a corrupt blob). Reject if nothing decoded.
			const result = await heic2any({
				blob: file,
				toType: 'image/jpeg',
				quality: 0.9,
			});
			const converted = Array.isArray(result) ? result[0] : result;
			if (!converted) {
				throw new Error('HEIC decode produced no image data');
			}

			const jpegName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
			return new File([converted], jpegName, { type: 'image/jpeg' });
		} catch (error) {
			logger.error('HEIC to JPEG conversion failed:', error);
			throw new Error(
				'Could not read this HEIC image. Please convert it to JPG or PNG and try again.',
			);
		}
	}

	isValidFile(
		file: File,
		customValidation?: (file: File) => boolean,
	): FileValidationResult {
		const filename = file.name;
		const fileSize = file.size;
		const ext = this.getFileExtension(filename);

		if (!ALLOWED_EXTENSIONS.has(ext)) {
			return {
				isValid: false,
				reason: 'format',
				error: `File extension ${ext} not allowed. Supported formats: JPG, PNG, MP4, MOV, CSV, Excel (.xlsx)`,
			};
		}

		if (customValidation && !customValidation(file)) {
			return {
				isValid: false,
				reason: 'format',
				error: 'The file does not meet the validation criteria',
			};
		}

		// Excel workbooks ride the contact-import path and convert to CSV, so they
		// share the CSV size budget and error wording rather than the image cap.
		const usesCsvCap = ext === '.csv' || ext === '.xlsx';
		const maxSize = usesCsvCap
			? MAX_CSV_SIZE
			: this.isVideoFile(filename)
				? MAX_VIDEO_SIZE
				: MAX_IMAGE_SIZE;

		if (fileSize > maxSize) {
			const sizeMB = maxSize / (1024 * 1024);
			const fileType = usesCsvCap
				? 'CSV/Excel files'
				: this.isVideoFile(filename)
					? 'videos'
					: 'images';
			return {
				isValid: false,
				reason: 'size',
				error: `File size exceeds maximum allowed size of ${sizeMB}MB for ${fileType}`,
			};
		}

		return {
			isValid: true,
		};
	}

	isValidImageFile(file: File): FileValidationResult {
		const ext = this.getFileExtension(file.name);
		if (!ALLOWED_IMAGE_EXTENSION_SET.has(ext)) {
			return {
				isValid: false,
				reason: 'format',
				error: `File extension ${ext} not allowed. Supported image formats: JPG, JPEG, PNG`,
			};
		}

		const normalizedMimeType = file.type.trim().toLowerCase();
		const resolvedMimeType =
			IMAGE_MIME_TYPE_ALIASES[normalizedMimeType] ?? normalizedMimeType;
		if (
			resolvedMimeType &&
			!ALLOWED_IMAGE_MIME_TYPE_SET.has(resolvedMimeType)
		) {
			return {
				isValid: false,
				reason: 'format',
				error: `File type ${file.type} not allowed. Supported image types: image/jpeg, image/png`,
			};
		}

		if (file.size > MAX_IMAGE_SIZE) {
			const sizeMB = MAX_IMAGE_SIZE / (1024 * 1024);
			return {
				isValid: false,
				reason: 'size',
				error: `File size exceeds maximum allowed size of ${sizeMB}MB for images`,
			};
		}

		return { isValid: true };
	}

	getFileExtension(filename: string): string {
		return `.${filename.toLowerCase().split('.').pop()}`;
	}

	/** Stored blob name: random UUID plus extension from the uploaded file name. */
	generateUniqueFilename(uploadedFileName: string): string {
		const lastDot = uploadedFileName.lastIndexOf('.');
		const hasExt = lastDot > 0;
		const ext = hasExt ? uploadedFileName.slice(lastDot).toLowerCase() : '';
		return `${crypto.randomUUID()}${ext}`;
	}

	// Create a preview URL for a file (for images and videos)
	// Before users upload a file we should give them preview
	// TODO: we may want to validate the file size and type here instead of during the upload and reject them
	async createPreview(
		file: File,
	): Promise<{ previewUrl: string | null; error?: string }> {
		try {
			file = await this.convertHeicToJpeg(file);
		} catch (error) {
			return {
				previewUrl: null,
				error: error instanceof Error ? error.message : 'Unknown error',
			};
		}

		const { isValid, error } = this.isValidFile(file);
		if (!isValid) {
			return { previewUrl: null, error };
		}

		return this.readPreviewDataUrl(file);
	}

	async createImagePreview(
		file: File,
	): Promise<{ previewUrl: string | null; error?: string }> {
		try {
			file = await this.convertHeicToJpeg(file);
		} catch (error) {
			return {
				previewUrl: null,
				error: error instanceof Error ? error.message : 'Unknown error',
			};
		}

		const { isValid, error } = this.isValidImageFile(file);
		if (!isValid) {
			return { previewUrl: null, error };
		}

		return this.readPreviewDataUrl(file);
	}

	private async readPreviewDataUrl(
		file: File,
	): Promise<{ previewUrl: string | null; error?: string }> {
		return new Promise((resolve) => {
			const reader = new FileReader();
			reader.onload = (e) => {
				resolve({ previewUrl: e.target?.result as string });
			};
			reader.onerror = (e) =>
				resolve({ previewUrl: null, error: e.target?.error?.message });
			reader.readAsDataURL(file);
		});
	}

	async batchCreatePreviews(files: FileList | File[]): Promise<
		{
			preview: string | null;
			isValid: boolean;
			error?: string;
		}[]
	> {
		const fileArray = Array.from(files);
		const preparedFiles = await Promise.all(
			fileArray.map(async (file) => {
				const { previewUrl, error: previewError } =
					await this.createPreview(file);

				return {
					preview: previewUrl,
					isValid: previewUrl !== null,
					error: previewError,
				};
			}),
		);

		return preparedFiles;
	}
}

export const fileUploadService = new FileUploadService();
